/**
 * SWTOR item quality, as games displays it.
 *
 * Items themselves come from the SWTORBIT platform (`/v1/items`). Games' own
 * hand-managed Cartel catalogue ("Global Data → Cartel Items") was retired in
 * PLATFORM_INTEGRATION.md Phase 2; only this vocabulary remains, because award
 * snapshots store it and the `text-quality-*` classes are named after it.
 */
/**
 * SWTOR item-quality tiers, used to tint a Cartel item's displayed name.
 *
 * IMPORTANT: these string values are deliberately identical to the suffixes of
 * the `$color-quality-*` SCSS variables in @swtorbit/ui's scss/_variables.scss,
 * because every render site builds its class name as `text-quality-${quality}`
 * (@swtorbit/ui generates those utilities from its colour map).
 * Renaming a value here without renaming the SCSS variable silently drops the
 * tint rather than failing to compile.
 */
export const CARTEL_ITEM_QUALITIES = [
    'artifact',
    'cheap',
    'currency',
    'legacy',
    'legendary',
    'mission',
    'moddable',
    'premium',
    'prototype',
    'standard',
];
/** Cartel items are Artifact-grade unless an admin says otherwise. */
export const DEFAULT_CARTEL_ITEM_QUALITY = 'artifact';
/** Human-readable tier names for pickers. */
export const CARTEL_ITEM_QUALITY_LABELS = {
    artifact: 'Artifact',
    cheap: 'Cheap',
    currency: 'Currency',
    legacy: 'Legacy',
    legendary: 'Legendary',
    mission: 'Mission',
    moddable: 'Moddable',
    premium: 'Premium',
    prototype: 'Prototype',
    standard: 'Standard',
};
/**
 * Narrow an untrusted value to a quality tier, or `undefined` when it is absent
 * or unrecognised.
 *
 * Returning `undefined` rather than defaulting is load-bearing on the treasure
 * path: an award row is a frozen snapshot, so awards made before quality existed
 * carry nothing here and must keep rendering in their original plain white — not
 * retroactively adopt a colour they were never won with. Callers that genuinely
 * want a fallback (the catalogue itself) apply `DEFAULT_CARTEL_ITEM_QUALITY`
 * explicitly.
 */
export function toCartelItemQuality(raw) {
    return typeof raw === 'string' && CARTEL_ITEM_QUALITIES.includes(raw)
        ? raw
        : undefined;
}
/** Platform display-quality codes whose games name differs (lower-cased). */
const PLATFORM_QUALITY_ALIASES = {
    quest: 'mission',
};
/**
 * Games' quality tier for a platform item's `displayQuality.code` (`Artifact`,
 * `Quest` …), or `undefined` when there is none or it is unrecognised.
 *
 * The platform's codes are the game's own; nine of the ten match games' tiers
 * once lower-cased, and the game's `Quest` is the tier games calls `mission`.
 */
export function platformQualityToCartelItemQuality(code) {
    if (!code)
        return undefined;
    const lower = code.toLowerCase();
    return PLATFORM_QUALITY_ALIASES[lower] ?? toCartelItemQuality(lower);
}
